import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Copy, Users, X, Sparkle, FileText, GraduationCap, ArrowSquareOut, UploadSimple, FileArrowUp, ClipboardText, Bell, Printer } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TASK_CODES } from "./GuidelinesPage";
import ComplianceTimeline from "../components/ComplianceTimeline";
import { TrainingRegister } from "../components/TrainingRegister";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const CADENCE_OPTIONS = [[0, "Once (no renewal)"], [12, "Annual (renews at hire anniversary)"]];
const SERIES_OPTIONS = ["Competency Training", "Orientation Series", "Competency Exam", "Improving Lives the Silver Homecare Way"];

const emptyTopic = () => ({
  title: "", description: "", video_url: "", guideline_id: "", mode: "reinforcement", resource_links: [],
  task_codes: [], passing_score: 70, max_attempts: 3, cadence_months: 12, due_days: 30,
  series: "Competency Training", state_mandated: false, active: true, questions: [],
});

const emptyForm = () => ({ title: "", description: "", fields: [], cadence_months: 12, updates_tb_date: false, active: true });

const ROSTER_PILL = {
  overdue: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]",
  never: "border-stone-300 bg-stone-100 text-stone-500",
  due_soon: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]",
  current: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]",
};
const ROSTER_LABEL = { overdue: "Overdue", never: "Missing", due_soon: "Due soon", current: "Current" };

export default function TrainingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [searchParams, setSearchParams] = useSearchParams();
  const seriesFilter = searchParams.get("series") || "";
  const [tab, setTab] = useState(searchParams.get("tab") || (isAdmin ? "tracker" : "topics"));

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);
  const [tracker, setTracker] = useState(null);
  const [progress, setProgress] = useState(null);
  const [registerFor, setRegisterFor] = useState(null);
  const [peopleSort, setPeopleSort] = useState("worst");
  const [trackerOpen, setTrackerOpen] = useState("");
  const [trackView, setTrackView] = useState("people");
  const [personOpen, setPersonOpen] = useState("");
  const [gaps, setGaps] = useState(null);
  const [dueReport, setDueReport] = useState(null);
  const [dueBusy, setDueBusy] = useState(false);

  const toggleTrackerTopic = (tid) => setTrackerOpen((cur) => (cur === tid ? "" : tid));
  const [rosterFor, setRosterFor] = useState(null);
  const [roster, setRoster] = useState(null);

  const toggleRoster = async (t) => {
    if (rosterFor === t.id) { setRosterFor(null); setRoster(null); return; }
    setRosterFor(t.id);
    setRoster(null);
    try {
      const { data } = await api.get(`/training/topics/${t.id}/roster`);
      setRoster(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRosterFor(null);
    }
  };
  const [topics, setTopics] = useState(null);
  const [unmatched, setUnmatched] = useState(null);
  const [assignTo, setAssignTo] = useState({});
  const [staffOpts, setStaffOpts] = useState([]);

  const loadUnmatched = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/training/completions/unmatched");
      setUnmatched(data);
      if (data.length && !staffOpts.length) {
        const res = await api.get("/users", { params: { page_size: 300 } });
        const list = Array.isArray(res.data) ? res.data : res.data.users || [];
        setStaffOpts(list.filter((u) => u.role === "staff" && u.active !== false));
      }
    } catch { /* queue card stays hidden */ }
  }, [isAdmin, staffOpts.length]);

  const assignCompletion = async (c) => {
    try {
      await api.post(`/training/completions/${c.id}/assign`, { user_id: assignTo[c.id] });
      toast.success(`Credit applied — ${c.topic_title} matched to the employee`);
      setUnmatched((list) => list.filter((x) => x.id !== c.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [forms, setForms] = useState(null);
  const [guidelines, setGuidelines] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [genCount, setGenCount] = useState(5);
  const [genBusy, setGenBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [completionsFor, setCompletionsFor] = useState(null);
  const [completions, setCompletions] = useState(null);
  const [recordsFor, setRecordsFor] = useState(null);
  const [records, setRecords] = useState(null);
  const [onboardingModules, setOnboardingModules] = useState([]);
  const [paperFor, setPaperFor] = useState(null);
  const [paperStaff, setPaperStaff] = useState([]);
  const [paperForm, setPaperForm] = useState({ user_id: "", score: "", completed_at: "", note: "", file: null });
  const evidenceInputRef = useRef(null);
  const evidenceTopicRef = useRef(null);
  const [evidenceBusy, setEvidenceBusy] = useState(false);

  useEffect(() => {
    if (tab === "tracker" && !gaps) {
      api.get("/training/compliance-gaps").then((r) => setGaps(r.data))
        .catch((e) => toast.error(formatApiError(e)));
    }
  }, [tab, trackView, gaps]); // eslint-disable-line

  useEffect(() => {
    if (tab === "tracker" && trackView === "due" && !dueReport) {
      api.get("/training/due-report").then((r) => setDueReport(r.data))
        .catch((e) => toast.error(formatApiError(e)));
    }
  }, [tab, trackView, dueReport]);

  const notifyDuePerson = async (p) => {
    try {
      const { data } = await api.post("/training/remind-due", { user_id: p.id });
      toast.success(`${data.reminded} reminder task(s) posted to ${p.name.split(" ")[0]}'s dashboard (in-app only — no emails)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const notifyDueAll = async () => {
    setDueBusy(true);
    try {
      const { data } = await api.post("/training/remind-due-all");
      toast.success(`${data.tasks} reminder task(s) posted across ${data.people} people — in-app dashboards only, no emails sent`);
    } catch (e) { toast.error(formatApiError(e)); }
    setDueBusy(false);
  };

  const openCount = (p) => (p.topics || []).reduce((m, t) => m + (t.gaps || []).filter((g) => g.still_open).length, 0);
  const histCount = (p) => (p.topics || []).reduce((m, t) => m + (t.gaps || []).filter((g) => !g.still_open).length, 0);
  const openGapTotal = (gaps?.people || []).reduce((n, p) => n + openCount(p), 0);
  const histGapTotal = (gaps?.people || []).reduce((n, p) => n + histCount(p), 0);

  const downloadGapsCsv = () => {
    if (!gaps) return;
    const rows = [["Employee", "Chain anchor", "Anchor source", "Training", "Paper dated from", "Paper dated to", "Still owed", "Completions on record"]];
    for (const p of gaps.people || []) {
      for (const t of p.topics) {
        for (const g of t.gaps) {
          rows.push([p.name, p.chain_anchor || "", p.chain_anchor_source || "", t.title,
                     g.needed_after, g.needed_by, g.still_open ? "yes" : "no",
                     (t.completions || []).join(" | ")]);
        }
      }
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "training-compliance-gaps.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const printGaps = () => {
    if (!gaps) return;
    const esc = (x) => String(x ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const today = new Date().toISOString().slice(0, 10);
    const people = [...(gaps.people || [])].sort((a, b) => openCount(b) - openCount(a));
    const personHtml = people.map((p) => {
      const meta = [openCount(p) > 0 && `${openCount(p)} OPEN`, histCount(p) > 0 && `${histCount(p)} historical`].filter(Boolean).join(" · ");
      const rows = p.topics.map((t) => {
        const gapsTxt = t.gaps.map((g) => (g.still_open
          ? `<span class="open">OVERDUE — complete now (window was ${esc(g.needed_after)} &rarr; ${esc(g.needed_by)})</span>`
          : `<span class="hist">missed window — paper dated ${esc(g.needed_after)} &rarr; ${esc(g.needed_by)} fixes the record</span>`)).join("<br>");
        return `<tr><td class="topic">${esc(t.title)}</td><td>${esc((t.completions || []).join(", ") || "none")}</td><td>${gapsTxt}</td></tr>`;
      }).join("");
      return `<h2>${esc(p.name)} <span class="meta">${esc(meta)}${meta ? " · " : ""}chain starts ${esc(p.chain_anchor || "unknown")}</span></h2>
        <table><thead><tr><th class="c1">Training</th><th class="c2">Completions on record</th><th class="c3">Gap windows</th></tr></thead><tbody>${rows}</tbody></table>`;
    }).join("");
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><title>Training gap report — ${today}</title><style>
      @page{size:letter portrait;margin:11mm}
      body{font-family:Georgia,serif;margin:0;color:#1c1917;font-size:12px;line-height:1.45}
      h1{font-size:18px;margin:0} .sub{color:#78716c;font-size:11px;margin:4px 0 16px}
      .legend{font-size:11.5px;margin:0 0 14px;padding:8px 10px;border:1px solid #e7e5e4;border-radius:6px}
      .legend b.open{color:#C0392B} .legend b.hist{color:#8a5d14}
      h2{font-size:14px;margin:16px 0 4px;page-break-after:avoid}
      h2 .meta{font-weight:normal;color:#78716c;font-size:10.5px}
      table{width:100%;border-collapse:collapse;table-layout:fixed;page-break-inside:auto}
      th{text-align:left;color:#78716c;text-transform:uppercase;font-size:9px;letter-spacing:.05em;padding:4px 8px;border-bottom:1.5px solid #6a3e97}
      td{padding:5px 8px;border-bottom:1px solid #e7e5e4;vertical-align:top;font-size:12px;word-wrap:break-word}
      th.c1{width:24%} th.c2{width:30%} th.c3{width:46%}
      td.topic{font-weight:bold}
      tr{page-break-inside:avoid}
      .open{color:#C0392B;font-weight:bold} .hist{color:#8a5d14}
      </style></head><body>
      <h1>Silver Home Care — Training gap report</h1>
      <p class="sub">Printed ${today} · ${people.length} people with gaps · A paper completion dated inside a shown window makes that period compliant again.</p>
      <p class="legend"><b class="open">${openGapTotal} open</b> — action needed NOW to be compliant &nbsp;|&nbsp; <b class="hist">${histGapTotal} historical</b> — past lapses to document with paper signatures</p>
      ${personHtml}
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const generateTaskQuizzes = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/training/task-guidelines/generate-quizzes");
      if (data.started) {
        toast.success(`Drafting quizzes for ${data.topics} task trainings in the background — refresh in a couple of minutes`);
      } else {
        toast.info(data.message || "Nothing to generate");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenBusy(false);
    }
  };

  const uploadEvidence = async (files) => {
    if (!files?.length || !evidenceTopicRef.current) return;
    setEvidenceBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append(f.name.toLowerCase().endsWith(".zip") && files.length === 1 ? "file" : "files", f);
      const { data } = await api.post(`/training-evidence/bulk-import?topic_id=${evidenceTopicRef.current}`, fd, { timeout: 300000 });
      toast.success(`Evidence imported: ${data.imported} attached${data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicates skipped` : ""}${data.unmatched_name_count ? `, ${data.unmatched_name_count} unmatched` : ""}${data.no_date_count ? `, ${data.no_date_count} without a readable completion date` : ""}`);
      if (data.unmatched_names?.length) toast.warning(`Unmatched: ${data.unmatched_names.slice(0, 5).join(", ")}`);
      if (data.no_date_files?.length) toast.warning(`No completion date found — left unattached: ${data.no_date_files.slice(0, 5).join(", ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEvidenceBusy(false);
      evidenceTopicRef.current = null;
    }
  };

  const load = useCallback(() => {
    api.get("/training/topics").then((r) => setTopics(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/screenings").then((r) => setForms(r.data)).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api/formatApiError are stable module imports
  }, []);
  useEffect(() => {
    load();
    loadUnmatched();
    if (isAdmin) api.get("/training/tracker").then((r) => setTracker(r.data)).catch(() => {});
    api.get("/training/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/guidelines").then((r) => setGuidelines(r.data)).catch(() => {});
    api.get("/onboarding/modules").then((r) => setOnboardingModules(r.data.modules || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; api/setGuidelines/setOnboardingModules are stable
  }, [load]);

  const copyLink = (path) => {
    navigator.clipboard.writeText(`${window.location.origin}${path}`);
    toast.success("Link copied — share it with staff");
  };

  const openCompletions = async (kind, item) => {
    setCompletionsFor({ kind, item });
    setCompletions(null);
    try {
      const url = kind === "topic" ? `/training/topics/${item.id}/completions` : `/screenings/${item.id}/completions`;
      const { data } = await api.get(url);
      setCompletions(kind === "topic" ? data : { submissions: data, legacy: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openRecords = async (t) => {
    setRecordsFor(t);
    setRecords(null);
    try {
      const { data } = await api.get(`/training/topics/${t.id}/evidence`);
      setRecords(data.records || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const viewRecord = async (r) => {
    try {
      const res = await api.get(`/users/${r.user_id}/training-evidence/${r.evidence_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPaper = async (t) => {
    setPaperFor(t);
    setPaperForm({ user_id: "", score: "", completed_at: new Date().toISOString().slice(0, 10), note: "", file: null });
    if (paperStaff.length === 0) {
      try {
        const { data } = await api.get("/users");
        setPaperStaff(data.filter((u) => u.role !== "admin"));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  const submitPaper = async () => {
    if (!paperForm.user_id || !paperForm.file || paperForm.score === "") {
      toast.error("Employee, score, and the scanned test are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", paperForm.user_id);
      fd.append("score", String(paperForm.score));
      fd.append("completed_at", paperForm.completed_at);
      fd.append("note", paperForm.note);
      fd.append("file", paperForm.file);
      const { data } = await api.post(`/training/topics/${paperFor.id}/paper-completion`, fd, { timeout: 60000 });
      toast.success(data.passed ? "Paper completion recorded" : `Recorded as a failed attempt (below ${data.passing_score}%)`);
      setPaperFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const [videoUploading, setVideoUploading] = useState(false);

  const uploadTopicVideo = async (f) => {
    if (!f || !editing?.id) return;
    setVideoUploading(true);
    try {
      const ext = (f.name.split(".").pop() || "mp4").toLowerCase();
      const { data: start } = await api.post("/recordings/start", { title: f.name.replace(/\.[^.]+$/, ""), ext });
      const cs = start.chunk_size;
      for (let off = 0, idx = 0; off < f.size; off += cs, idx++) {
        await api.post(`/recordings/chunk?id=${start.id}&index=${idx}`, f.slice(off, off + cs),
          { headers: { "Content-Type": "application/octet-stream" } });
      }
      await api.post("/recordings/finish", { id: start.id });
      const { data: att } = await api.post(`/recordings/${start.id}/attach`, { topic_id: editing.id });
      setEditing((t) => ({ ...t, video_url: att.video_url }));
      toast.success("Video uploaded and attached to this topic");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVideoUploading(false); }
  };

  const saveTopic = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = {
        title: editing.title, description: editing.description, video_url: editing.video_url,
        resource_links: editing.resource_links || [],
        guideline_id: editing.guideline_id, mode: editing.mode, task_codes: editing.task_codes || [], passing_score: editing.passing_score,
        max_attempts: editing.max_attempts, cadence_months: editing.cadence_months,
        due_days: editing.due_days, series: editing.series, state_mandated: editing.state_mandated,
        annual_order: editing.annual_order || 0,
        rollout_date: editing.rollout_date || "", due_date: editing.due_date || "",
        active: editing.active, questions: editing.questions,
      };
      if (editing.id) {
        await api.put(`/training/topics/${editing.id}`, payload);
        toast.success("Training topic saved");
      } else {
        await api.post("/training/topics", payload);
        toast.success("Training topic created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteTopic = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? Past completions stay on employee records.`)) return;
    try {
      await api.delete(`/training/topics/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateQuiz = async () => {
    if (!editing.id) { toast.error("Save the topic first, then generate questions"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post(`/training/topics/${editing.id}/generate-quiz`, { count: genCount, guideline_id: editing.guideline_id });
      setEditing((e) => ({ ...e, questions: [...(e.questions || []), ...data.questions] }));
      toast.success(`Drafted ${data.questions.length} questions — review them, then save`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenBusy(false);
    }
  };

  const saveForm = async () => {
    if (!editingForm.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = {
        title: editingForm.title, description: editingForm.description, fields: editingForm.fields,
        cadence_months: editingForm.cadence_months, updates_tb_date: editingForm.updates_tb_date, active: editingForm.active,
      };
      if (editingForm.id) {
        await api.put(`/screenings/${editingForm.id}`, payload);
        toast.success("Screening form saved");
      } else {
        await api.post("/screenings", payload);
        toast.success("Screening form created");
      }
      setEditingForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteForm = async (f) => {
    if (!window.confirm(`Delete "${f.title}"? Past submissions stay on employee records.`)) return;
    try {
      await api.delete(`/screenings/${f.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setQ = (idx, patch) => setEditing((e) => ({ ...e, questions: e.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) }));
  const setF = (idx, patch) => setEditingForm((f) => ({ ...f, fields: f.fields.map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));

  if (!topics || !forms) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const modeBadge = (m) => m === "graded"
    ? <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-xs font-medium text-[#8a5d14]">Graded competency</span>
    : <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-xs font-medium text-[#1e8449]">Reinforcement</span>;

  return (
    <div className="max-w-4xl" data-testid="training-page">
      <input ref={evidenceInputRef} type="file" accept=".pdf,.zip" multiple className="hidden"
        onChange={(e) => { uploadEvidence(e.target.files); e.target.value = ""; }} data-testid="topic-evidence-input" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Training Center</h1>
          <p className="mt-1 text-sm text-stone-500">Video trainings with quizzes, screening forms, and employee attestations. Share the links — no login needed.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={generateTaskQuizzes} disabled={genBusy} className="border-stone-300" data-testid="generate-task-quizzes-button">
              <Sparkle size={15} className="mr-1.5" /> {genBusy ? "Starting…" : "Draft task quizzes (AI)"}
            </Button>
            <Button onClick={() => (tab === "topics" ? setEditing(emptyTopic()) : setEditingForm(emptyForm()))} data-testid="new-item-button">
              <Plus size={16} className="mr-1.5" /> {tab === "topics" ? "New training topic" : "New screening form"}
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6 flex gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm" data-testid="training-tabs">
        {[...(isAdmin ? [["tracker", "Tracker", ClipboardText]] : []), ["topics", isAdmin ? "Training topics" : "My trainings", GraduationCap], ...(isAdmin ? [["screenings", "Screening forms", FileText]] : [])].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${tab === key ? "bg-primary text-primary-foreground" : "text-stone-600 hover:bg-accent"}`}
            data-testid={`tab-${key}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {isAdmin && (unmatched || []).length > 0 && (
        <div className="mb-6 rounded-xl border border-[#B7791F]/40 bg-[#B7791F]/5 p-4 shadow-sm" data-testid="unmatched-completions-card">
          <p className="font-heading text-sm font-bold text-[#8a5d14]">
            {unmatched.length} signed completion{unmatched.length === 1 ? "" : "s"} waiting to be matched
          </p>
          <p className="mt-0.5 text-xs text-stone-500">The employee ID entered didn't match a record. Pick the right person — the credit applies instantly.</p>
          <div className="mt-3 space-y-2">
            {unmatched.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 bg-white p-3" data-testid={`unmatched-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-900">{c.name} <span className="text-stone-400">· ID entered: {c.employee_id}</span></p>
                  <p className="text-xs text-stone-500">{c.topic_title} · {c.score}% · {String(c.signed_at || "").slice(0, 10)}</p>
                </div>
                <select className="rounded-md border border-stone-300 bg-white px-2 py-1.5 text-xs" value={assignTo[c.id] || ""}
                  onChange={(e) => setAssignTo((m) => ({ ...m, [c.id]: e.target.value }))} data-testid={`unmatched-assign-select-${c.id}`}>
                  <option value="">Match to employee…</option>
                  {staffOpts.map((s) => <option key={s.id} value={s.id}>{s.name}{s.profile?.employee_id ? ` (#${s.profile.employee_id})` : ""}</option>)}
                </select>
                <Button size="sm" disabled={!assignTo[c.id]} onClick={() => assignCompletion(c)}
                  className="rounded-full text-xs" data-testid={`unmatched-assign-btn-${c.id}`}>Apply credit</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "tracker" && isAdmin && (
        <div className="space-y-3" data-testid="training-tracker">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-stone-500">
              Every training, who it's missing for, and who's due — driven by the completion date on record
              {tracker ? ` (${tracker.staff_count} active staff)` : ""}.
              {tracker && <span className="ml-1 font-semibold text-stone-600" data-testid="tracker-last-import"> Training data last imported: {tracker.last_import || "—"}</span>}
            </p>
            <div className="flex items-center gap-2">
            {trackView === "people" && (
              <select value={peopleSort} onChange={(e) => setPeopleSort(e.target.value)}
                className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="tracker-people-sort">
                <option value="worst">Sort: worst first</option>
                <option value="name">Sort: name A–Z</option>
                <option value="competency">Sort: annual trainings status</option>
                <option value="tb">Sort: TB status</option>
                <option value="orientation">Sort: orientation status</option>
              </select>
            )}
            <div className="flex gap-1 rounded-full border border-stone-200 p-0.5" data-testid="tracker-view-toggle">
              {[["people", "By person"], ["topics", "By training"], ["due", "Due report"], ["timeline", "Compliance timeline"], ["gaps", openGapTotal > 0 ? `Gap report — ${openGapTotal} need action now` : "Gap report"]].map(([v, l]) => (
                <button key={v} onClick={() => setTrackView(v)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${trackView === v ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"}`}
                  data-testid={`tracker-view-${v}`}>{l}</button>
              ))}
            </div>
            </div>
          </div>
          {!tracker && <p className="text-xs text-stone-400">Loading tracker…</p>}

          {trackView === "people" && tracker && (
            <div className="space-y-2" data-testid="tracker-by-person">
              {[...(tracker.people || [])].sort((a, b) => {
                const pa = (progress?.people || []).find((x) => x.user_id === a.id);
                const pb = (progress?.people || []).find((x) => x.user_id === b.id);
                const rank = { red: 0, yellow: 1, green: 2 };
                if (peopleSort === "name") return (a.name || "").localeCompare(b.name || "");
                if (peopleSort === "worst") return (rank[pa?.overall] ?? 3) - (rank[pb?.overall] ?? 3) || (a.name || "").localeCompare(b.name || "");
                const ra = rank[pa?.tracks?.[peopleSort]?.status] ?? 3;
                const rb = rank[pb?.tracks?.[peopleSort]?.status] ?? 3;
                return ra - rb || (a.name || "").localeCompare(b.name || "");
              }).map((p) => {
                const pg = ((progress?.people || []).find((x) => x.user_id === p.id)) || null;
                const om = { green: ["border-l-[#27AE60]", "In full compliance", "text-[#1e8449]"],
                             yellow: ["border-l-[#B7791F]", "Coming close", "text-[#8a5d14]"],
                             red: ["border-l-[#C0392B]", "Out of compliance", "text-[#C0392B]"] }[pg?.overall] || null;
                return (
                <div key={p.id} className={`rounded-lg border border-stone-200 bg-white p-4 shadow-sm ${om ? `border-l-4 ${om[0]}` : ""}`} data-testid={`tracker-person-${p.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link to={`/team/${p.id}`} className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-stone-800 hover:text-primary hover:underline">{p.name}
                        {om && <span className={`ml-2 text-[10px] font-bold uppercase tracking-wide ${om[2]}`} data-testid={`tracker-status-${p.id}`}>{om[1]}</span>}
                      </p>
                      {pg && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`tracker-pills-${p.id}`}>
                          {Object.entries(pg.tracks).map(([key, t]) => (
                            <button key={key} onClick={(e) => { e.preventDefault(); setRegisterFor({ uid: p.id, name: p.name, track: key === "orientation" ? "" : key, label: t.label }); }}
                              className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-shadow hover:shadow ${
                              { green: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]",
                                yellow: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]",
                                red: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]",
                                gray: "border-stone-200 bg-stone-50 text-stone-400" }[t.status]}`}
                              title={t.status === "gray" ? "Not tracked — click for the register" : `${t.done} current · ${t.due_soon} due soon · ${t.overdue} overdue — click for the register`}
                              data-testid={`tracker-pill-${key}-${p.id}`}>
                              {t.label} {t.total ? `${t.done}/${t.total}` : "—"}
                              {t.next_due && <span className="ml-1 opacity-75">· next {t.next_due.slice(5, 10).replace("-", "/")}/{t.next_due.slice(2, 4)}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="block h-1.5 w-full max-w-48 overflow-hidden rounded-full bg-stone-100">
                          <span className="block h-full rounded-full bg-[#1e8449]" style={{ width: `${Math.round((p.done / Math.max(1, p.total)) * 100)}%` }} />
                        </span>
                        <span className="text-[11px] text-stone-400">{p.done} of {p.total} current{p.records > 0 && p.done === 0 ? ` · all expired (last ${p.last_done})` : p.records === 0 ? " · never on record" : ""}{p.records > 0 ? ` · proof on file: ${p.with_cert}/${p.records}` : ""}</span>
                      </div>
                      {p.records > 0 && (p.lapsed_topics || []).length === 0 && (
                        <p className="mt-1 text-[10px] font-medium text-[#1e8449]" data-testid={`tracker-person-chain-${p.id}`}>
                          {p.outstanding.length === 0 ? "compliant all the way through ✓" : "no broken renewals on record ✓"}
                        </p>
                      )}
                      {(p.lapsed_topics || []).length > 0 && (
                        <p className="mt-1 text-[10px] font-medium text-[#8a5d14]" data-testid={`tracker-person-chain-${p.id}`}>
                          chain broke before ({p.lapsed_topics.length} training{p.lapsed_topics.length === 1 ? "" : "s"} had a gap over a year): {p.lapsed_topics.slice(0, 3).join(", ")}{p.lapsed_topics.length > 3 ? ` +${p.lapsed_topics.length - 3} more` : ""}
                        </p>
                      )}
                      {p.records > 0 && p.chain_anchor_source === "competency_exam" && (
                        <p className="mt-0.5 text-[10px] text-stone-400" data-testid={`tracker-person-anchor-${p.id}`}>
                          365-day chain anchored on competency exam ({p.chain_anchor}) — no start date on file
                        </p>
                      )}
                      {p.records > 0 && !p.chain_anchor && (
                        <p className="mt-0.5 text-[10px] text-stone-400" data-testid={`tracker-person-anchor-${p.id}`}>
                          add a start date or competency exam date to verify the first-completion window
                        </p>
                      )}
                    </Link>
                    {p.outstanding.length === 0 ? (
                      <span className="rounded-full border border-[#27AE60]/40 bg-[#27AE60]/10 px-2.5 py-0.5 text-xs font-semibold text-[#1e8449]" data-testid={`tracker-person-clear-${p.id}`}>All done</span>
                    ) : (
                      <button onClick={() => setPersonOpen((cur) => (cur === p.id ? "" : p.id))}
                        className="rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2.5 py-0.5 text-xs font-semibold text-[#C0392B]"
                        data-testid={`tracker-person-outstanding-${p.id}`}>
                        {p.outstanding.length} outstanding
                      </button>
                    )}
                  </div>
                  {personOpen === p.id && p.outstanding.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5 border-t border-stone-100 pt-3" data-testid={`tracker-person-list-${p.id}`}>
                      {p.outstanding.map((o) => (
                        <span key={o.topic_id} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${o.status === "overdue" ? "border-[#C0392B]/30 bg-[#C0392B]/5 text-[#C0392B]" : "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"}`}
                          data-testid={`tracker-person-topic-${p.id}-${o.topic_id}`}>
                          {o.title}{o.completed_as ? ` · last done as “${o.completed_as}”` : ""}{o.renew_by ? ` · was due ${o.renew_by}` : " · never on record"}
                          <button onClick={async (e) => { e.preventDefault(); try {
                              const { data: r } = await api.post("/training/remind", { user_id: p.id, topic_id: o.topic_id });
                              toast.success(r.email === "sent" ? `Reminder emailed to ${p.name.split(" ")[0]}` : `Reminder posted to ${p.name.split(" ")[0]}'s dashboard (emails are off)`);
                            } catch (err) { toast.error(formatApiError(err)); } }}
                            className="ml-1 rounded-full p-0.5 hover:bg-stone-200" title="Send reminder"
                            data-testid={`tracker-remind-${p.id}-${o.topic_id}`}><Bell size={11} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {trackView === "due" && (
            <div className="space-y-2" data-testid="due-report">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 bg-white p-4">
                <p className="max-w-2xl text-xs text-stone-500">
                  <strong>Required trainings (the 7 + HIPAA) are due before a caregiver's start date</strong>, then renew yearly —
                  reminder at day 325, must finish by day 345. This report lists everyone <strong>due right now or within 30 days</strong>.
                  Notifications post a task to the person's dashboard — <strong>no emails are sent</strong>.
                </p>
                <Button size="sm" onClick={notifyDueAll} disabled={dueBusy || !dueReport || !(dueReport?.people || []).length}
                  className="bg-primary text-primary-foreground" data-testid="due-notify-all">
                  <Bell size={14} className="mr-1.5" /> {dueBusy ? "Posting…" : "Notify everyone (dashboard tasks)"}
                </Button>
              </div>
              {!dueReport && <p className="text-xs text-stone-400">Building the due report…</p>}
              {dueReport && (dueReport.people || []).length === 0 && (
                <p className="rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-4 text-xs font-medium text-[#1e8449]" data-testid="due-report-empty">
                  Nobody is due right now or within 30 days — all required trainings are covered.
                </p>
              )}
              {(dueReport?.people || []).map((p) => (
                <div key={p.id} className={`rounded-lg border border-stone-200 bg-white p-3.5 shadow-sm border-l-4 ${p.worst === "overdue" ? "border-l-[#C0392B]" : "border-l-[#B7791F]"}`}
                  data-testid={`due-person-${p.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-stone-800">
                        <Link to={`/team/${p.id}`} className="hover:text-primary hover:underline">{p.name}</Link>
                        {p.employee_id && <span className="ml-1.5 text-xs font-normal text-stone-400">#{p.employee_id}</span>}
                        <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.worst === "overdue" ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" : "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"}`}
                          data-testid={`due-worst-${p.id}`}>{p.worst === "overdue" ? "Due now" : "Due within 30 days"}</span>
                      </p>
                      {p.start_date && (
                        <p className="mt-0.5 text-[11px] text-stone-400" data-testid={`due-start-${p.id}`}>start date {p.start_date}</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {p.items.map((i) => (
                          <span key={i.topic_id} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${i.status === "overdue" ? "border-[#C0392B]/30 bg-[#C0392B]/5 text-[#C0392B]" : "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"}`}
                            data-testid={`due-item-${p.id}-${i.topic_id}`}>
                            {i.title}{i.completed_as ? ` · last done as “${i.completed_as}”` : ""}{i.renew_by ? ` · due ${i.renew_by}` : " · due now"}{i.last_done ? ` · last ${i.last_done}` : " · never on record"}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => notifyDuePerson(p)} className="border-stone-300 text-xs"
                      data-testid={`due-notify-${p.id}`}>
                      <Bell size={13} className="mr-1" /> Notify
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {trackView === "timeline" && <ComplianceTimeline />}

          {trackView === "gaps" && (
            <div className="space-y-2" data-testid="gap-report">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 bg-white p-4">
                <p className="max-w-2xl text-xs text-stone-500">
                  Every broken link in each person's 365-day chain — a paper completion <strong>dated inside the window shown</strong> makes that period compliant again. Use this when collecting backdated signatures.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={printGaps} disabled={!gaps} data-testid="gap-print-button">Print report</Button>
                  <Button size="sm" onClick={downloadGapsCsv} disabled={!gaps} className="bg-primary text-primary-foreground" data-testid="gap-download-csv">Download CSV</Button>
                </div>
              </div>
              {!gaps && <p className="text-xs text-stone-400">Building the gap report…</p>}
              {gaps && (gaps.people || []).length === 0 && (
                <p className="rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-4 text-xs font-medium text-[#1e8449]" data-testid="gap-report-empty">
                  Every chain on record is unbroken — nothing to chase.
                </p>
              )}
              {gaps && (gaps.people || []).length > 0 && (
                <div className="rounded-lg border border-stone-200 bg-white p-3 text-xs" data-testid="gap-summary">
                  <span className="font-semibold text-[#C0392B]">{openGapTotal} open — action needed NOW to be compliant</span>
                  <span className="mx-2 text-stone-300">|</span>
                  <span className="font-semibold text-[#8a5d14]">{histGapTotal} historical — past lapses to document with paper signatures</span>
                </div>
              )}
              {[...(gaps?.people || [])].sort((a, b) => openCount(b) - openCount(a)).map((p) => (
                <div key={p.id} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`gap-person-${p.id}`}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <Link to={`/team/${p.id}`} className="text-sm font-semibold text-stone-800 hover:text-primary hover:underline">{p.name}</Link>
                    {openCount(p) > 0 && <span className="rounded-full bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]" data-testid={`gap-count-${p.id}`}>{openCount(p)} OPEN — act now</span>}
                    {histCount(p) > 0 && <span className="rounded-full bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#8a5d14]">{histCount(p)} historical — find paper</span>}
                    {p.chain_anchor ? (
                      <span className="text-[10px] text-stone-400">
                        chain starts {p.chain_anchor}{p.chain_anchor_source === "competency_exam" ? " (competency exam — no start date on file)" : " (start date)"}
                      </span>
                    ) : (
                      <span className="text-[10px] text-stone-400">no start date or competency date — first window can't be dated</span>
                    )}
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {p.topics.map((t) => (
                      <div key={t.topic_id} className="text-xs" data-testid={`gap-topic-${p.id}-${t.topic_id}`}>
                        <span className="font-medium text-stone-700">{t.title}</span>
                        <span className="text-stone-400"> · on record: {(t.completions || []).join(", ") || "none"}</span>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {t.gaps.map((g, i) => (
                            <span key={i} className={`rounded-full border px-2.5 py-0.5 text-[11px] ${g.still_open ? "border-[#C0392B]/40 bg-[#C0392B]/5 font-semibold text-[#C0392B]" : "border-[#B7791F]/40 bg-[#B7791F]/5 text-[#8a5d14]"}`}
                              data-testid={`gap-window-${p.id}-${t.topic_id}-${i}`}>
                              {g.still_open
                                ? `OVERDUE — complete now (window was ${g.needed_after} → ${g.needed_by})`
                                : `missed window — paper dated ${g.needed_after} → ${g.needed_by} fixes the record`}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {trackView === "topics" && (tracker?.topics || []).map((t) => (
            <div key={t.id} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`tracker-topic-${t.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-stone-800">{t.title}</p>
                  <p className="mt-0.5 text-xs text-stone-400">
                    {t.cadence_months ? `Renews every ${t.cadence_months} months` : "One-time"} · last submitted: <strong className="text-stone-600">{t.last_submitted || "—"}</strong>
                    {t.rollout_date && <span className="ml-1 rounded-full bg-[#B7791F]/10 px-1.5 py-0.5 font-semibold text-[#8a5d14]" data-testid={`tracker-rollout-${t.id}`}>rolls out {t.rollout_date}{t.due_date ? ` · due ${t.due_date}` : ""}</span>}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
                  <span className="rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2 py-0.5 text-[#C0392B]" data-testid={`tracker-overdue-count-${t.id}`}>{t.counts.overdue} overdue</span>
                  <span className="rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-stone-500" data-testid={`tracker-never-count-${t.id}`}>{t.counts.never} never</span>
                  <span className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[#8a5d14]" data-testid={`tracker-duesoon-count-${t.id}`}>{t.counts.due_soon} due soon</span>
                  <span className="rounded-full border border-[#27AE60]/40 bg-[#27AE60]/10 px-2 py-0.5 text-[#1e8449]" data-testid={`tracker-current-count-${t.id}`}>{t.counts.current} current</span>
                  <button onClick={() => toggleTrackerTopic(t.id)} className="rounded-md border border-stone-300 px-2 py-1 text-stone-600 hover:bg-accent" data-testid={`tracker-expand-${t.id}`}>
                    {trackerOpen === t.id ? "Hide names" : "Names"}
                  </button>
                </div>
              </div>
              {trackerOpen === t.id && (
                <div className="mt-3 max-h-64 space-y-1 overflow-y-auto border-t border-stone-100 pt-2" data-testid={`tracker-names-${t.id}`}>
                  {[...t.overdue.map((r) => ({ ...r, s: "Overdue", cls: ROSTER_PILL.overdue })),
                    ...t.due_soon.map((r) => ({ ...r, s: "Due soon", cls: ROSTER_PILL.due_soon }))].map((r) => (
                    <div key={r.id + r.s} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 px-2.5 py-1.5 text-xs" data-testid={`tracker-row-${r.id}`}>
                      <span className="font-medium text-stone-700">{r.name}</span>
                      <span className="flex items-center gap-3 text-stone-500">
                        <span>last {r.last_done || "—"}{r.completed_as ? ` (“${r.completed_as}”)` : ""}</span>
                        <span>was due {r.renew_by || "—"}</span>
                        <a href={`${api.defaults.baseURL}/training/register/${r.id}/${t.id}/paper-packet`}
                          title="Download the paper training + quiz pre-filled for this person"
                          className="rounded-full border border-stone-200 p-1 text-stone-400 hover:border-primary hover:text-primary"
                          data-testid={`tracker-paper-${t.id}-${r.id}`}><Printer size={11} /></a>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.cls}`}>{r.s}</span>
                      </span>
                    </div>
                  ))}
                  {t.overdue.length === 0 && t.due_soon.length === 0 && (
                    <p className="text-xs text-[#1e8449]">Nobody overdue or due soon for this training.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "topics" && (
        <div data-testid="topics-list">
          {(() => {
            const counts = {};
            topics.forEach((t) => { const s = t.series || "Competency Training"; counts[s] = (counts[s] || 0) + 1; });
            const seriesNames = Object.keys(counts);
            if (seriesNames.length < 2) return null;
            return (
              <div className="mb-5 flex flex-wrap items-center gap-1.5" data-testid="series-chips">
                <span className="mr-1 text-xs font-medium text-stone-400">Series:</span>
                <button onClick={() => setSearchParams({ tab: "topics" })}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${!seriesFilter ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:border-primary/50"}`}
                  data-testid="series-chip-all">All ({topics.length})</button>
                {seriesNames.map((s) => (
                  <button key={s} onClick={() => setSearchParams({ tab: "topics", series: s })}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${seriesFilter.toLowerCase() === s.toLowerCase() ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:border-primary/50"}`}
                    data-testid={`series-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s} ({counts[s]})</button>
                ))}
              </div>
            );
          })()}
          {topics.length === 0 && <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-400">No training topics yet.</p>}
          {(() => {
            const card = (t, num) => (
            <div key={t.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`topic-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-medium text-stone-900">
                    {num != null && (
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground" data-testid={`annual-order-${num}`}>{num}</span>
                    )}
                    {t.title}
                    {modeBadge(t.mode)}
                    {(t.task_codes || []).length > 0
                      ? <span className="rounded-full border border-primary/30 bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">Task-linked: {t.task_codes.join(", ")}</span>
                      : <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">All staff</span>}
                    <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">{t.cadence_months > 0 ? "Annual" : "Once"} · due +{t.due_days ?? 30}d</span>
                    {t.state_mandated && <span className="rounded-full border border-[#8a5d14]/40 bg-[#8a5d14]/10 px-2 py-0.5 text-xs font-medium text-[#8a5d14]" data-testid={`topic-mandatory-${t.id}`}>Mandatory</span>}
                    {t.active === false && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-xs font-medium text-stone-500">Inactive</span>}
                    {t.quiz_draft && <span className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-xs font-medium text-[#8a5d14]" data-testid={`topic-draft-${t.id}`}>Draft quiz — review & publish</span>}
                  </p>
                  <p className="mt-1 text-xs text-stone-500">
                    {t.series || "Competency Training"} · {t.question_count} question{t.question_count === 1 ? "" : "s"}{t.mode === "graded" ? ` · passing ${t.passing_score}%` : ""}
                    {isAdmin && t.completion_count != null && ` · ${t.completion_count} completed`}
                    {isAdmin && t.evidence_count != null && (
                      <>
                        {" · "}
                        <button onClick={() => openRecords(t)} className="font-medium text-primary hover:underline" data-testid={`topic-records-${t.id}`}>
                          {t.evidence_count} uploaded record{t.evidence_count === 1 ? "" : "s"}
                        </button>
                      </>
                    )}
                  </p>
                  {!isAdmin && t.my_status?.passed_at && (
                    <p className="mt-1 text-xs font-medium text-[#1e8449]">You completed {String(t.my_status.passed_at).slice(0, 10)}{t.mode === "graded" ? ` (${t.my_status.score}%)` : ""}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <a href={`/train/${t.token}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`topic-open-${t.id}`}>
                    <ArrowSquareOut size={13} /> Open
                  </a>
                  <button onClick={() => copyLink(`/train/${t.token}`)}
                    className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`topic-copy-${t.id}`}>
                    <Copy size={13} /> Copy link
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => toggleRoster(t)} title="Per-person status: missing, due soon, overdue — with last-done and renew-by dates"
                        className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${rosterFor === t.id ? "border-primary bg-primary/10 text-primary" : "border-stone-300 text-stone-700 hover:bg-accent"}`} data-testid={`topic-roster-toggle-${t.id}`}>
                        <Users size={13} /> Who's due
                      </button>
                      <button onClick={() => openCompletions("topic", t)}
                        className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`topic-completions-${t.id}`}>
                        <Users size={13} /> Completions
                      </button>
                      <button onClick={() => openPaper(t)} title="Record a paper test for a staff member"
                        className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`topic-paper-${t.id}`}>
                        <UploadSimple size={13} /> Paper
                      </button>
                      <button onClick={() => { evidenceTopicRef.current = t.id; evidenceInputRef.current?.click(); }} title="Upload signed evidence PDFs/ZIP for this training (matches staff by filename)"
                        className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`topic-evidence-${t.id}`}>
                        <FileArrowUp size={13} /> Evidence
                      </button>
                      <button onClick={() => setEditing({ ...t, questions: t.questions || [] })}
                        className="rounded-md border border-stone-300 p-1.5 text-stone-600 hover:bg-accent" data-testid={`topic-edit-${t.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => deleteTopic(t)}
                        className="rounded-md border border-stone-300 p-1.5 text-stone-600 hover:text-[#C0392B]" data-testid={`topic-delete-${t.id}`}><Trash size={14} /></button>
                    </>
                  )}
                </div>
              </div>
              {rosterFor === t.id && (
                <div className="mt-3 border-t border-stone-100 pt-3" data-testid={`topic-roster-${t.id}`}>
                  {!roster ? (
                    <p className="text-xs text-stone-400">Loading roster…</p>
                  ) : (
                    <>
                      <p className="mb-2 text-xs font-medium text-stone-500" data-testid={`roster-summary-${t.id}`}>
                        <span className="text-[#C0392B]">{roster.counts.overdue || 0} overdue</span> · <span className="text-stone-500">{roster.counts.never || 0} never done</span> · <span className="text-[#8a5d14]">{roster.counts.due_soon || 0} due soon</span> · <span className="text-[#1e8449]">{roster.counts.current || 0} current</span> — renews every {roster.topic.cadence_months} months
                      </p>
                      <div className="max-h-72 space-y-1 overflow-y-auto">
                        {roster.rows.map((r) => (
                          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 px-2.5 py-1.5 text-xs" data-testid={`roster-row-${r.id}`}>
                            <span className="font-medium text-stone-700">{r.name}</span>
                            <span className="flex items-center gap-3 text-stone-500">
                              <span>{r.last_done ? `last ${r.last_done}` : "no record"}</span>
                              <span>{r.renew_by ? `due ${r.renew_by}` : "—"}</span>
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${ROSTER_PILL[r.status]}`} data-testid={`roster-status-${r.id}`}>{ROSTER_LABEL[r.status]}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            );
            const visible = seriesFilter
              ? topics.filter((t) => (t.series || "Competency Training").toLowerCase() === seriesFilter.toLowerCase())
              : topics;
            const mandated = visible.filter((t) => t.state_mandated).sort((a, b) => (a.annual_order || 99) - (b.annual_order || 99) || a.title.localeCompare(b.title));
            const others = visible.filter((t) => !t.state_mandated);
            const seriesOrder = [];
            const bySeries = {};
            for (const t of others) {
              const s = t.series || "Competency Training";
              if (!bySeries[s]) { bySeries[s] = []; seriesOrder.push(s); }
              bySeries[s].push(t);
            }
            return (
              <>
                {seriesFilter && (
                  <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm text-stone-700" data-testid="series-filter-banner">
                    Showing only: <strong>{seriesFilter}</strong>
                    <Link to="/training?tab=topics" className="ml-auto font-medium text-primary hover:underline" data-testid="series-filter-clear">Show all training</Link>
                  </div>
                )}
                {mandated.length > 0 && (
                  <div className="mb-8" data-testid="topics-annual-group">
                    <h2 className="font-heading text-lg font-semibold text-stone-900">Primary mandatory trainings</h2>
                    <p className="mb-3 mt-0.5 text-xs text-stone-500">The 7 annual mandated topics, TB screening/education, and the competency exam — every caregiver renews these yearly from their own completion dates.</p>
                    <div className="space-y-3">{mandated.map((t) => card(t, t.annual_order))}</div>
                  </div>
                )}
                {seriesOrder.map((s) => (
                  <div key={s} className="mb-8" data-testid={`topics-series-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <h2 className="mb-3 font-heading text-base font-semibold text-stone-700">{s}</h2>
                    <div className="space-y-3">{bySeries[s].map((t) => card(t))}</div>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      )}

      {tab === "screenings" && isAdmin && (
        <div className="space-y-3" data-testid="screenings-list">
          {forms.length === 0 && <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-400">No screening forms yet.</p>}
          {forms.map((f) => (
            <div key={f.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`screening-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-medium text-stone-900">
                    {f.title}
                    {f.cadence_months > 0 && <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">Every {f.cadence_months} mo</span>}
                    {f.updates_tb_date && <span className="rounded-full border border-primary/30 bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">Updates TB date</span>}
                    {f.active === false && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-xs font-medium text-stone-500">Inactive</span>}
                  </p>
                  <p className="mt-1 text-xs text-stone-500">
                    {(f.fields || []).length} fields
                    {isAdmin && f.completion_count != null && ` · ${f.completion_count} submitted`}
                  </p>
                  {!isAdmin && f.my_status?.signed_at && (
                    <p className="mt-1 text-xs font-medium text-[#1e8449]">You completed this {String(f.my_status.signed_at).slice(0, 10)}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <a href={`/screen/${f.token}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`screening-open-${f.id}`}>
                    <ArrowSquareOut size={13} /> Open
                  </a>
                  <button onClick={() => copyLink(`/screen/${f.token}`)}
                    className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`screening-copy-${f.id}`}>
                    <Copy size={13} /> Copy link
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => openCompletions("screening", f)}
                        className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`screening-completions-${f.id}`}>
                        <Users size={13} /> Submissions
                      </button>
                      <button onClick={() => setEditingForm({ ...f, fields: (f.fields || []).map((x) => ({ ...x, options_text: (x.options || []).join("\n") })) })}
                        className="rounded-md border border-stone-300 p-1.5 text-stone-600 hover:bg-accent" data-testid={`screening-edit-${f.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => deleteForm(f)}
                        className="rounded-md border border-stone-300 p-1.5 text-stone-600 hover:text-[#C0392B]" data-testid={`screening-delete-${f.id}`}><Trash size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" data-testid="topic-editor">
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-900">{editing.id ? "Edit training topic" : "New training topic"}</h2>
              <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-900" data-testid="topic-editor-close"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="tp-title">Title</Label>
                <Input id="tp-title" value={editing.title} onChange={(e) => setEditing((t) => ({ ...t, title: e.target.value }))} data-testid="topic-title" />
              </div>
              <div>
                <Label htmlFor="tp-desc">Description (shown above the video)</Label>
                <Textarea id="tp-desc" rows={2} value={editing.description} onChange={(e) => setEditing((t) => ({ ...t, description: e.target.value }))} data-testid="topic-description" />
              </div>
              <div>
                <Label htmlFor="tp-video">Video URL (YouTube or Vimeo link)</Label>
                <Input id="tp-video" placeholder="https://youtube.com/watch?v=..." value={editing.video_url} onChange={(e) => setEditing((t) => ({ ...t, video_url: e.target.value }))} data-testid="topic-video-url" />
                {editing.id && (
                  <div className="mt-2">
                    <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:border-primary ${videoUploading ? "opacity-50" : ""}`} data-testid="topic-video-upload">
                      {videoUploading ? "Uploading…" : "Or upload a video file from your computer"}
                      <input type="file" accept="video/*" className="hidden" disabled={videoUploading} data-testid="topic-video-file-input"
                        onChange={(e) => uploadTopicVideo(e.target.files?.[0])} />
                    </label>
                    <p className="mt-1 text-[11px] text-stone-400">MP4/MOV — uploads in the background and attaches to this topic.</p>
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="tp-links">Training video links with attestation (optional)</Label>
                <p className="mb-1 text-[11px] text-stone-400">One per line: Title | URL — the trainee must confirm "I watched and understood" for each before continuing.</p>
                <Textarea id="tp-links" rows={4} placeholder={"Bathing & Grooming | https://youtube.com/watch?v=...\nInfection Control | https://vimeo.com/..."}
                  value={editing.linksText ?? (editing.resource_links || []).map((l) => `${l.title} | ${l.url}`).join("\n")}
                  onChange={(e) => setEditing((t) => ({
                    ...t, linksText: e.target.value,
                    resource_links: e.target.value.split("\n").map((l) => {
                      const [title, ...rest] = l.split("|");
                      return { title: title.trim(), url: rest.join("|").trim() };
                    }).filter((l) => l.url),
                  }))}
                  data-testid="topic-resource-links" />
              </div>
              <div>
                <Label htmlFor="tp-guideline">Linked guideline (used as AI quiz source)</Label>
                <select id="tp-guideline" value={editing.guideline_id} onChange={(e) => setEditing((t) => ({ ...t, guideline_id: e.target.value }))}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="topic-guideline">
                  <option value="">None</option>
                  {guidelines.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select>
              </div>
              <div>
                <Label>Task codes — makes this a task-linked training</Label>
                <p className="mt-0.5 text-xs text-stone-500">
                  Task-linked trainings are required only for staff serving clients whose care plan includes these tasks — and must be completed before serving them. Leave all unchecked for a mandatory all-staff training.
                </p>
                <div className="mt-2 grid max-h-40 grid-cols-2 gap-1 overflow-y-auto rounded-lg border border-stone-200 p-3 sm:grid-cols-3" data-testid="topic-task-codes">
                  {TASK_CODES.map(([code, label]) => {
                    const on = (editing.task_codes || []).includes(code);
                    return (
                      <label key={code} className="flex items-center gap-1.5 text-xs text-stone-700" data-testid={`topic-task-${code}`}>
                        <input type="checkbox" checked={on} className="h-3.5 w-3.5 accent-[#2D6A4F]"
                          onChange={(e) => setEditing((t) => ({ ...t, task_codes: e.target.checked ? [...(t.task_codes || []), code] : (t.task_codes || []).filter((x) => x !== code) }))} />
                        {code} {label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Quiz style</Label>
                <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                  {[["reinforcement", "Reinforcement (annual trainings)", "Wrong answers show the correct one with an explanation and can be changed — the point is learning + attestation, not pass/fail."],
                    ["graded", "Graded (initial competency)", "No hints during the quiz. Graded pass/fail with limited attempts; the office is notified if someone runs out."]].map(([val, label, desc]) => (
                    <button key={val} type="button" onClick={() => setEditing((t) => ({ ...t, mode: val }))}
                      className={`rounded-lg border p-3 text-left transition-colors ${editing.mode === val ? "border-primary bg-accent" : "border-stone-200 hover:border-stone-400"}`}
                      data-testid={`topic-mode-${val}`}>
                      <p className="text-sm font-medium text-stone-900">{label}</p>
                      <p className="mt-0.5 text-xs text-stone-500">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="tp-pass">Passing score %</Label>
                  <Input id="tp-pass" type="number" min="0" max="100" value={editing.passing_score} onChange={(e) => setEditing((t) => ({ ...t, passing_score: e.target.value }))} data-testid="topic-passing-score" />
                </div>
                <div>
                  <Label htmlFor="tp-attempts">Max attempts (graded)</Label>
                  <Input id="tp-attempts" type="number" min="1" max="10" value={editing.max_attempts} onChange={(e) => setEditing((t) => ({ ...t, max_attempts: e.target.value }))} data-testid="topic-max-attempts" />
                </div>
                <div>
                  <Label htmlFor="tp-cadence">Frequency</Label>
                  <select id="tp-cadence" value={editing.cadence_months} onChange={(e) => setEditing((t) => ({ ...t, cadence_months: Number(e.target.value) }))}
                    className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="topic-cadence">
                    {CADENCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="tp-series">Series</Label>
                  <select id="tp-series" value={editing.series} onChange={(e) => setEditing((t) => ({ ...t, series: e.target.value }))}
                    className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="topic-series">
                    {SERIES_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="tp-due">Due (days after hire / anniversary)</Label>
                  <Input id="tp-due" type="number" min="0" max="365" value={editing.due_days} onChange={(e) => setEditing((t) => ({ ...t, due_days: e.target.value }))} data-testid="topic-due-days" />
                </div>
                <div className="flex items-end gap-3 pb-1.5">
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input type="checkbox" checked={!!editing.state_mandated} onChange={(e) => setEditing((t) => ({ ...t, state_mandated: e.target.checked }))} className="h-4 w-4 accent-[#2D6A4F]" data-testid="topic-state-mandated" />
                    Mandatory (primary) training
                  </label>
                  {editing.state_mandated && (
                    <label className="flex items-center gap-1.5 text-xs text-stone-500">
                      Order
                      <input type="number" min="1" max="99" value={editing.annual_order || ""} onChange={(e) => setEditing((t) => ({ ...t, annual_order: e.target.value }))}
                        className="h-8 w-16 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="topic-annual-order" />
                    </label>
                  )}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="tp-rollout">Rollout date (optional — hidden from counts until then)</Label>
                  <Input id="tp-rollout" type="date" value={editing.rollout_date || ""} onChange={(e) => setEditing((t) => ({ ...t, rollout_date: e.target.value }))} data-testid="topic-rollout-date" />
                </div>
                <div>
                  <Label htmlFor="tp-duedate">Completion deadline (optional)</Label>
                  <Input id="tp-duedate" type="date" value={editing.due_date || ""} onChange={(e) => setEditing((t) => ({ ...t, due_date: e.target.value }))} data-testid="topic-due-date" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing((t) => ({ ...t, active: e.target.checked }))} className="h-4 w-4 accent-[#2D6A4F]" data-testid="topic-active" />
                Active (link works)
              </label>

              <div className="rounded-lg border border-stone-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-stone-900">Quiz questions ({(editing.questions || []).length})</p>
                  <div className="flex items-center gap-2">
                    <select value={genCount} onChange={(e) => setGenCount(Number(e.target.value))}
                      className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="topic-gen-count">
                      {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <Button size="sm" variant="outline" onClick={generateQuiz} disabled={genBusy} data-testid="topic-generate-quiz">
                      <Sparkle size={14} className="mr-1" /> {genBusy ? "Generating..." : "Generate with AI"}
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => setEditing((t) => ({ ...t, questions: [...(t.questions || []), { id: `q${Date.now()}`, text: "", choices: ["", "", "", ""], correct_index: 0, explanation: "" }] }))}
                      data-testid="topic-add-question">
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                </div>
                {editing.id == null && <p className="mt-1 text-xs text-stone-400">Save the topic first to use AI generation (it reads the linked guideline).</p>}
                <div className="mt-3 space-y-4">
                  {(editing.questions || []).map((q, idx) => (
                    <div key={q.id || idx} className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid={`question-editor-${idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <Input placeholder={`Question ${idx + 1}`} value={q.text} onChange={(e) => setQ(idx, { text: e.target.value })} className="bg-white" data-testid={`question-text-${idx}`} />
                        <button onClick={() => setEditing((t) => ({ ...t, questions: t.questions.filter((_, i) => i !== idx) }))}
                          className="mt-1.5 text-stone-400 hover:text-[#C0392B]" data-testid={`question-delete-${idx}`}><Trash size={15} /></button>
                      </div>
                      <div className="mt-2 space-y-1.5">
                        {q.choices.map((c, ci) => (
                          <div key={`${ci}-${(c || "").slice(0, 16)}`} className="flex items-center gap-2">
                            <input type="radio" name={`correct-${idx}`} checked={q.correct_index === ci} onChange={() => setQ(idx, { correct_index: ci })}
                              className="h-4 w-4 accent-[#2D6A4F]" data-testid={`question-correct-${idx}-${ci}`} title="Mark as correct answer" />
                            <Input placeholder={`Choice ${ci + 1}`} value={c}
                              onChange={(e) => setQ(idx, { choices: q.choices.map((x, i) => (i === ci ? e.target.value : x)) })}
                              className="h-8 bg-white text-sm" data-testid={`question-choice-${idx}-${ci}`} />
                            {q.choices.length > 2 && (
                              <button onClick={() => setQ(idx, { choices: q.choices.filter((_, i) => i !== ci), correct_index: q.correct_index > ci ? q.correct_index - 1 : Math.min(q.correct_index, q.choices.length - 2) })}
                                className="text-stone-300 hover:text-[#C0392B]" data-testid={`question-choice-delete-${idx}-${ci}`}><X size={13} /></button>
                            )}
                          </div>
                        ))}
                        {q.choices.length < 6 && (
                          <button onClick={() => setQ(idx, { choices: [...q.choices, ""] })}
                            className="text-xs font-medium text-primary hover:underline" data-testid={`question-add-choice-${idx}`}>+ Add choice</button>
                        )}
                      </div>
                      <Textarea rows={2} placeholder="Explanation shown after answering (teaches the point)" value={q.explanation}
                        onChange={(e) => setQ(idx, { explanation: e.target.value })} className="mt-2 bg-white text-xs" data-testid={`question-explanation-${idx}`} />
                      {editing.series === "Competency Exam" && (
                        <div className="mt-2">
                          <Label>Onboarding topic section (used for rewatch assignments)</Label>
                          <select value={q.onboarding_module_id || ""} onChange={(e) => setQ(idx, { onboarding_module_id: e.target.value })}
                            className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid={`question-module-${idx}`}>
                            <option value="">No remediation topic</option>
                            {onboardingModules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} data-testid="topic-cancel">Cancel</Button>
                <Button onClick={saveTopic} disabled={busy} data-testid="topic-save">{busy ? "Saving..." : "Save topic"}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" data-testid="screening-editor">
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-900">{editingForm.id ? "Edit screening form" : "New screening form"}</h2>
              <button onClick={() => setEditingForm(null)} className="text-stone-400 hover:text-stone-900" data-testid="screening-editor-close"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="sf-title">Title</Label>
                <Input id="sf-title" value={editingForm.title} onChange={(e) => setEditingForm((f) => ({ ...f, title: e.target.value }))} data-testid="screening-title" />
              </div>
              <div>
                <Label htmlFor="sf-desc">Description / purpose</Label>
                <Textarea id="sf-desc" rows={2} value={editingForm.description} onChange={(e) => setEditingForm((f) => ({ ...f, description: e.target.value }))} data-testid="screening-description" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="sf-cadence">Renewal cadence</Label>
                  <select id="sf-cadence" value={editingForm.cadence_months} onChange={(e) => setEditingForm((f) => ({ ...f, cadence_months: Number(e.target.value) }))}
                    className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="screening-cadence">
                    {CADENCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input type="checkbox" checked={!!editingForm.updates_tb_date} onChange={(e) => setEditingForm((f) => ({ ...f, updates_tb_date: e.target.checked }))} className="h-4 w-4 accent-[#2D6A4F]" data-testid="screening-updates-tb" />
                    Completion sets "Last TB screening date"
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input type="checkbox" checked={editingForm.active !== false} onChange={(e) => setEditingForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-[#2D6A4F]" data-testid="screening-active" />
                    Active (link works)
                  </label>
                </div>
              </div>

              <div className="rounded-lg border border-stone-200 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-stone-900">Form fields ({(editingForm.fields || []).length})</p>
                  <Button size="sm" variant="outline"
                    onClick={() => setEditingForm((f) => ({ ...f, fields: [...(f.fields || []), { id: `f${Date.now()}`, type: "checkboxes", label: "", options_text: "" }] }))}
                    data-testid="screening-add-field">
                    <Plus size={14} className="mr-1" /> Add field
                  </Button>
                </div>
                <div className="mt-3 space-y-4">
                  {(editingForm.fields || []).map((f, idx) => (
                    <div key={f.id || idx} className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid={`field-editor-${idx}`}>
                      <div className="flex items-center gap-2">
                        <select value={f.type} onChange={(e) => setF(idx, { type: e.target.value })}
                          className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid={`field-type-${idx}`}>
                          <option value="checkboxes">Checkboxes</option>
                          <option value="date">Date</option>
                          <option value="text">Short text</option>
                        </select>
                        <Input placeholder="Field label / question" value={f.label} onChange={(e) => setF(idx, { label: e.target.value })} className="bg-white" data-testid={`field-label-${idx}`} />
                        <button onClick={() => setEditingForm((x) => ({ ...x, fields: x.fields.filter((_, i) => i !== idx) }))}
                          className="text-stone-400 hover:text-[#C0392B]" data-testid={`field-delete-${idx}`}><Trash size={15} /></button>
                      </div>
                      {f.type === "checkboxes" && (
                        <Textarea rows={4} placeholder={"One option per line, e.g.:\nPersistent cough lasting 3 weeks or more\nNONE OF THE ABOVE"}
                          value={f.options_text ?? (f.options || []).join("\n")} onChange={(e) => setF(idx, { options_text: e.target.value })}
                          className="mt-2 bg-white text-xs" data-testid={`field-options-${idx}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingForm(null)} data-testid="screening-cancel">Cancel</Button>
                <Button onClick={() => {
                  const cleaned = { ...editingForm, fields: (editingForm.fields || []).map((f) => ({ ...f, options: (f.options_text ?? (f.options || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean) })) };
                  setEditingForm(cleaned);
                  setTimeout(() => {
                    if (!cleaned.title.trim()) { toast.error("Title is required"); return; }
                    setBusy(true);
                    const payload = { title: cleaned.title, description: cleaned.description, fields: cleaned.fields, cadence_months: cleaned.cadence_months, updates_tb_date: cleaned.updates_tb_date, active: cleaned.active };
                    const req = cleaned.id ? api.put(`/screenings/${cleaned.id}`, payload) : api.post("/screenings", payload);
                    req.then(() => { toast.success(cleaned.id ? "Screening form saved" : "Screening form created"); setEditingForm(null); load(); })
                      .catch((e) => toast.error(formatApiError(e)))
                      .finally(() => setBusy(false));
                  }, 0);
                }} disabled={busy} data-testid="screening-save">{busy ? "Saving..." : "Save form"}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paperFor && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" data-testid="paper-modal">
          <div className="mx-auto my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Record a paper test</h2>
              <button onClick={() => setPaperFor(null)} className="text-stone-400 hover:text-stone-900" data-testid="paper-close"><X size={20} /></button>
            </div>
            <p className="text-xs text-stone-500">
              For staff who took <span className="font-medium text-stone-700">{paperFor.title}</span> on paper. Upload the graded test — it becomes their completion record and counts toward compliance (needs {paperFor.passing_score}%+).
            </p>
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <select value={paperForm.user_id} onChange={(e) => setPaperForm({ ...paperForm, user_id: e.target.value })}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="paper-employee-select">
                  <option value="">Select employee...</option>
                  {paperStaff.map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email}{u.profile?.employee_id ? ` (ID ${u.profile.employee_id})` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Score (%)</Label>
                  <Input type="number" min="0" max="100" value={paperForm.score} onChange={(e) => setPaperForm({ ...paperForm, score: e.target.value })}
                    className="border-stone-300 bg-white" data-testid="paper-score-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Date taken</Label>
                  <Input type="date" value={paperForm.completed_at} onChange={(e) => setPaperForm({ ...paperForm, completed_at: e.target.value })}
                    className="border-stone-300 bg-white" data-testid="paper-date-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Scanned test (PDF or photo)</Label>
                <input type="file" accept=".pdf,image/*" onChange={(e) => setPaperForm({ ...paperForm, file: e.target.files?.[0] || null })}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-accent file:px-3 file:py-1 file:text-xs file:font-medium"
                  data-testid="paper-file-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Input value={paperForm.note} onChange={(e) => setPaperForm({ ...paperForm, note: e.target.value })}
                  placeholder="e.g. administered in person at office" className="border-stone-300 bg-white" data-testid="paper-note-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPaperFor(null)} className="border-stone-300 text-stone-500" data-testid="paper-cancel">Cancel</Button>
                <Button onClick={submitPaper} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="paper-submit">
                  {busy ? "Saving..." : "Record Completion"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {recordsFor && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" data-testid="records-modal">
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-900">Uploaded records — {recordsFor.title}</h2>
              <button onClick={() => { setRecordsFor(null); setRecords(null); }} className="text-stone-400 hover:text-stone-900" data-testid="records-close"><X size={20} /></button>
            </div>
            {records === null && <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}
            {records !== null && records.length === 0 && <p className="py-6 text-center text-sm text-stone-400">No uploaded records for this training yet. Use the Evidence button on the topic to upload signed PDFs or a ZIP.</p>}
            <div className="space-y-2">
              {(records || []).map((r) => (
                <div key={r.evidence_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 p-3" data-testid={`record-${r.evidence_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900">{r.user_name}</p>
                    <p className="truncate text-xs text-stone-500">uploaded {String(r.uploaded_at || "").slice(0, 10)}{r.source ? ` · ${r.source}` : ""}</p>
                  </div>
                  <button onClick={() => viewRecord(r)} className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`record-view-${r.evidence_id}`}>
                    <FileText size={12} /> View
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {completionsFor && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" data-testid="completions-modal">
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-900">
                {completionsFor.kind === "topic" ? "Completions" : "Submissions"} — {completionsFor.item.title}
              </h2>
              <button onClick={() => { setCompletionsFor(null); setCompletions(null); }} className="text-stone-400 hover:text-stone-900" data-testid="completions-close"><X size={20} /></button>
            </div>
            {completions === null && <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}
            {completions !== null && !(completions.submissions?.length || completions.legacy?.length) && <p className="py-6 text-center text-sm text-stone-400">Nothing on record yet.</p>}
            {(completions?.submissions || []).length > 0 && (
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500" data-testid="submissions-section">
                {completionsFor.kind === "topic" ? `Signed submissions in-app (${completions.submissions.length})` : `Submissions (${completions.submissions.length})`}
              </h3>
            )}
            <div className="space-y-2">
              {(completions?.submissions || []).map((c) => (
                <div key={c.id} className="rounded-lg border border-stone-200 p-3" data-testid={`completion-${c.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="font-medium text-stone-900">{c.name} <span className="text-xs font-normal text-stone-400">ID {c.employee_id}</span></span>
                    <span className="text-xs text-stone-500">{String(c.signed_at).slice(0, 10)}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-stone-500">
                    <span>{c.email}</span>
                    {completionsFor.kind === "topic" && (
                      <>
                        <span>· attempt {c.attempt}</span>
                        {c.paper && <span className="rounded-full bg-stone-100 px-2 py-0.5 font-medium text-stone-600">paper · <a href={`${api.defaults.baseURL}/training/completions/${c.id}/paper-scan`} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid={`completion-scan-${c.id}`}>view scan</a></span>}
                        {c.passed
                          ? <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 font-medium text-[#1e8449]">Completed {c.score}%</span>
                          : <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2 py-0.5 font-medium text-[#C0392B]">Failed {c.score}%</span>}
                      </>
                    )}
                  </div>
                  {completionsFor.kind === "topic" && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`completion-actions-${c.id}`}>
                      {c.has_signature && (
                        <a href={`${api.defaults.baseURL}/training/completions/${c.id}/submission`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2 py-1 text-xs font-medium text-stone-700 hover:bg-accent"
                          data-testid={`completion-submission-${c.id}`}><FileText size={12} /> Signed submission</a>
                      )}
                      {c.passed && c.has_certificate && (
                        <a href={`${api.defaults.baseURL}/training/completions/${c.id}/certificate`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-accent px-2 py-1 text-xs font-medium text-accent-foreground hover:bg-accent/70"
                          data-testid={`completion-certificate-${c.id}`}><GraduationCap size={12} /> Certificate</a>
                      )}
                    </div>
                  )}
                  {completionsFor.kind === "screening" && c.responses && (
                    <div className="mt-2 space-y-1 border-t border-stone-100 pt-2">
                      {(completionsFor.item.fields || []).map((f) => {
                        const v = c.responses[f.id];
                        const display = Array.isArray(v) ? (v.length ? v.join("; ") : "—") : (v || "—");
                        return (
                          <p key={f.id} className="text-xs text-stone-600" data-testid={`completion-response-${c.id}-${f.id}`}>
                            <span className="font-medium text-stone-800">{f.label}</span> {display}
                          </p>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {completionsFor.kind === "topic" && (completions?.legacy || []).length > 0 && (
              <div className="mt-5" data-testid="legacy-records-section">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Imported completion records ({completions.legacy.length})
                </h3>
                <div className="space-y-2">
                  {completions.legacy.map((l) => (
                    <div key={l.user_id} className="rounded-lg border border-stone-200 p-3" data-testid={`legacy-record-${l.user_id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="font-medium text-stone-900">{l.name} <span className="text-xs font-normal text-stone-400">ID {l.employee_id || "—"}</span></span>
                        <span className="text-xs text-stone-500">{String(l.passed_at).slice(0, 10)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(l.evidence || []).map((e) => (
                          <a key={e.id} href={`${api.defaults.baseURL}/users/${l.user_id}/training-evidence/${e.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2 py-1 text-xs font-medium text-stone-700 hover:bg-accent"
                            data-testid={`legacy-evidence-${l.user_id}-${e.id}`}><FileText size={12} /> Signed form{e.form_date ? ` · ${String(e.form_date).slice(0, 10)}` : ""}</a>
                        ))}
                        {(l.evidence || []).length === 0 && (
                          <span className="text-xs text-stone-400" data-testid={`legacy-noevidence-${l.user_id}`}>No uploaded signed form on file</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {registerFor && (
        <TrainingRegister uid={registerFor.uid} name={registerFor.name} track={registerFor.track}
          label={registerFor.label} onClose={() => setRegisterFor(null)} />
      )}
    </div>
  );
}
